import jsPDF from 'jspdf';

const MAX_IMAGE_EDGE_PX = 1600;
const JPEG_QUALITY = 0.68;

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Não foi possível processar a imagem ${file.name}.`));
    };
    image.src = objectUrl;
  });

const renderCompressedPage = async (file: File) => {
  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;

  if (!sourceWidth || !sourceHeight) {
    throw new Error(`A imagem ${file.name} não possui dimensões válidas.`);
  }

  const scale = Math.min(1, MAX_IMAGE_EDGE_PX / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('O navegador não conseguiu preparar a página do exame.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  return {
    dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
    orientation: width > height ? ('landscape' as const) : ('portrait' as const),
    width,
    height,
  };
};

const sanitizePdfFileName = (title: string) => {
  const normalized = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

  return `${normalized || 'exame'}-${Date.now()}.pdf`;
};

export const createExamPdfFile = async (images: File[], title: string): Promise<File> => {
  if (images.length === 0) {
    throw new Error('Adicione pelo menos uma foto do exame.');
  }

  let pdf: jsPDF | null = null;

  for (const [index, file] of images.entries()) {
    const page = await renderCompressedPage(file);

    if (!pdf) {
      pdf = new jsPDF({
        orientation: page.orientation,
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
    } else {
      pdf.addPage('a4', page.orientation);
    }

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 7;
    const availableWidth = pageWidth - margin * 2;
    const availableHeight = pageHeight - margin * 2;
    const scale = Math.min(availableWidth / page.width, availableHeight / page.height);
    const renderWidth = page.width * scale;
    const renderHeight = page.height * scale;
    const x = (pageWidth - renderWidth) / 2;
    const y = (pageHeight - renderHeight) / 2;

    pdf.addImage(page.dataUrl, 'JPEG', x, y, renderWidth, renderHeight, `exam-page-${index}`, 'FAST');
  }

  if (!pdf) {
    throw new Error('Não foi possível gerar o PDF do exame.');
  }

  const blob = pdf.output('blob');
  return new File([blob], sanitizePdfFileName(title), { type: 'application/pdf' });
};
